import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';
import Request from '../../utils/request';
import { InputTypes, validFormElementValue } from '../../utils/validateFormElement';
import FormComponent from '../FormComponent';

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

/**
 * AddNewContact FormComponent
 * Autocomplete contact search field with a selected-contact card.
 * Used as a dependent question of the search-type radio.
 */
class AddNewContact extends FormComponent {
	constructor(containerElement, config) {
		super(containerElement, config);
		this.searchTimeout = null;
	}

	getSearchApiUrl() {
		return this.data?.api?.url || '/api/venueRegistration/teamInformation/members/search';
	}

	getSearchType() {
		return this.data?.searchType || 'name';
	}

	normaliseContact(contact) {
		if (!contact) {
			return null;
		}

		const nameValue = contact.name || '';
		const hasSplitLabel = nameValue.includes(' - ');
		const membershipFromLabel = hasSplitLabel ? nameValue.split(' - ')[0] : '';
		const nameFromLabel = hasSplitLabel ? nameValue.split(' - ')[1] : '';
		const displayName = contact.displayName || nameFromLabel || nameValue;
		const membershipNumber =
			contact.ltaNumber ||
			contact.ltaMembershipNumber ||
			contact.membershipId ||
			membershipFromLabel ||
			'';

		return {
			id: contact.contactId,
			displayName,
			membershipNumber,
			label: contact.name || displayName,
			role: contact.role
		};
	}

	getSelectedContact() {
		return this.data?.selectedContact || {};
	}

	markup(value, data = {}, titleMarkup) {
		const inputId = `add-new-contact-search-${data.id}`;
		const selectedContact = data.selectedContact;
		const label = data.label
			? `<label for="${inputId}" class="lta-text-input__label u-text-grey-lta">${data.label}</label>`
			: '';
		const description = data.description
			? `<div class="u-mb-1"><label for="${inputId}" class="lta-text-input__description u-text-grey-lta">${data.description}</label></div>`
			: '';

		return `
			<div class="lta-add-new-contact">
				<div class="lta-add-new-contact__title u-hidden">${titleMarkup}</div>
				<div class="lta-add-new-contact__search-field">
					${label}
					${description}
					${selectedContact ? this.renderSelectedContactMarkup(selectedContact, data) : this.renderSearchMarkup(inputId, data)}
				</div>
			</div>
		`;
	}

	renderSearchMarkup(inputId, data) {
		const searchResults = data.searchResults;
		const searchError = data.searchError;
		const showDropdown = data.isDropdownOpen;

		let dropdownContent = '';
		if (searchError) {
			dropdownContent = `<div class="lta-add-new-contact__dropdown-error">${searchError}</div>`;
		} else if (Array.isArray(searchResults) && searchResults.length === 0) {
			dropdownContent = `<div class="lta-add-new-contact__dropdown-empty">No results found</div>`;
		} else if (Array.isArray(searchResults)) {
			dropdownContent = searchResults
				.map(
					(result, index) => `
						<button type="button" class="lta-add-new-contact__dropdown-item js-add-new-contact-result" data-result-index="${index}">
							${result.name}
						</button>
					`
				)
				.join('');
		}

		return `
			<div class="lta-text-input__wrapper lta-add-new-contact__input-wrapper">
				<div class="lta-text-input--error lta-add-new-contact__search-error"></div>
				<input
					type="text"
					class="lta-text-input u-mb-1 js-add-new-contact-search-input"
					id="${inputId}"
					autocomplete="off"
					value="${data.searchQuery || ''}"
				/>
				<div class="lta-add-new-contact__dropdown" style="display: ${showDropdown ? 'block' : 'none'};">
					${dropdownContent}
				</div>
			</div>
		`;
	}

	getMembershipNumberLabel(searchType) {
		const labels = {
			ltaMembership: 'LTA membership number',
			coachCode: 'Coach code'
		};

		return labels[searchType] || '';
	}

	renderSelectedContactMarkup(contact, data = {}) {
		const membershipNumberLabel = this.getMembershipNumberLabel(data.searchType);

		return `
			<div class="lta-add-new-contact__selected">
				<div class="lta-add-new-contact__selected-name">${contact.displayName}</div>
				${
					contact.membershipNumber
						? `<div class="lta-add-new-contact__selected-meta">${
								membershipNumberLabel ? `${membershipNumberLabel}: ` : ''
						  }${contact.membershipNumber}</div>`
						: ''
				}
				<button type="button" class="lta-btn lta-btn--xs lta-btn--petite lta-btn--icon lta-btn--icon-before js-add-new-contact-reset u-mt-2">
					<svg class="lta-icon js-lta-text-input--btn-icon u-mr-1" width="1.5em" height="1.5em" fill="currentColor" alt="search" aria-hidden="true">
						<use xlink:href="/static/images/icons/lta-icons.svg#close"></use>
					</svg>Select a different contact
				</button>
			</div>
		`;
	}

	afterRender() {
		clearTimeout(this.blurTimeout);
		this._isReset = false;

		this.rootEl = SelectorEngine.findOne('.lta-add-new-contact', this.containerEl);
		this.inputWrapper = SelectorEngine.findOne('.lta-add-new-contact__input-wrapper', this.rootEl);
		this.searchInput = SelectorEngine.findOne('.js-add-new-contact-search-input', this.rootEl);

		if (this.searchInput) {
			EventHandler.on(this.searchInput, 'input', () => this.handleSearchInput());
			EventHandler.on(this.searchInput, 'blur', () => {
				clearTimeout(this.blurTimeout);
				this.blurTimeout = setTimeout(() => {
					if (!this.isActive()) {
						return;
					}

					// Re-render destroys the input and fires blur — skip close if focus stayed in the component.
					if (this.rootEl?.contains(document.activeElement)) {
						return;
					}

					if (this.data.isDropdownOpen) {
						this.updateData({ isDropdownOpen: false });
					}
				}, 200);
			});
		}

		const resetButton = SelectorEngine.findOne('.js-add-new-contact-reset', this.rootEl);
		if (resetButton) {
			EventHandler.on(resetButton, 'click', () => this.clearSelectedContact());
		}

		SelectorEngine.find('.js-add-new-contact-result', this.rootEl).forEach((button) => {
			EventHandler.on(button, 'mousedown', (event) => event.preventDefault());
			EventHandler.on(button, 'click', () => {
				const result = this.data.searchResults?.[Number(button.dataset.resultIndex)];
				if (result) {
					this.selectContact(result);
				}
			});
		});

		if (this.data.restoreSearchFocus && this.searchInput) {
			this.searchInput.focus();
			// Re-rendering replaces the input element, so focusing it resets the caret to
			// position 0 in most browsers. Explicitly move it to the end of the current
			// value, otherwise further typing inserts at the start (looks like RTL input).
			const caretPosition = this.searchInput.value.length;
			this.searchInput.setSelectionRange(caretPosition, caretPosition);
			this.data.restoreSearchFocus = false;
		}
	}

	handleSearchInput() {
		if (this._isReset) {
			return;
		}

		clearTimeout(this.searchTimeout);
		const searchQuery = this.searchInput?.value || '';
		const query = searchQuery.trim();

		if (query.length < SEARCH_MIN_LENGTH) {
			if (this.data.isDropdownOpen) {
				this.updateData({
					searchQuery,
					searchResults: null,
					searchError: null,
					isDropdownOpen: false,
					restoreSearchFocus: true
				});
			} else {
				this.data.searchQuery = searchQuery;
			}
			return;
		}

		this.data.searchQuery = searchQuery;
		this.searchTimeout = setTimeout(() => {
			this.performSearch(query);
		}, SEARCH_DEBOUNCE_MS);
	}

	async performSearch(query) {
		try {
			const response = await Request.get(this.getSearchApiUrl(), {
				query,
				searchType: this.getSearchType()
			});

			if (!this.isActive() || (this.data.searchQuery || '').trim() !== query) {
				return;
			}

			this.updateData({
				searchResults: response?.results || [],
				searchError: null,
				isDropdownOpen: true,
				restoreSearchFocus: true
			});
		} catch (error) {
			console.error('Member search failed:', error);

			if (!this.isActive() || (this.data.searchQuery || '').trim() !== query) {
				return;
			}

			this.updateData({
				searchResults: null,
				searchError: 'Search error. Please try again.',
				isDropdownOpen: true,
				restoreSearchFocus: true
			});
		}
	}

	selectContact(contact) {
		clearTimeout(this.searchTimeout);
		clearTimeout(this.blurTimeout);
		const selectedContact = this.normaliseContact(contact);

		this.updateData({
			selectedContact,
			searchQuery: '',
			searchResults: null,
			searchError: null,
			isDropdownOpen: false
		});
		this.emitValue(selectedContact);
		this.data?.onSelectedContactChange?.(selectedContact);
	}

	clearSelectedContact({ skipEmit = false } = {}) {
		clearTimeout(this.searchTimeout);
		clearTimeout(this.blurTimeout);

		const resetData = {
			selectedContact: null,
			searchQuery: '',
			searchResults: null,
			searchError: null,
			isDropdownOpen: false
		};

		if (skipEmit) {
			this._isReset = true;
			Object.assign(this.data, resetData);
			return;
		}

		this.updateData({
			...resetData,
			restoreSearchFocus: true
		});

		this.emitValue(null);
		this.data?.onSelectedContactChange?.(null);
	}

	isActive() {
		return !this._isReset;
	}

	render() {
		if (this._isReset) {
			return;
		}

		super.render();
	}

	updateData(_data) {
		if (this._isReset) {
			return;
		}

		super.updateData(_data);
	}

	remove() {
		this._isReset = true;
		clearTimeout(this.searchTimeout);
		clearTimeout(this.blurTimeout);
		super.remove();
	}

	emitValue(selectedContact = this.data?.selectedContact) {
		this.value = {
			fieldName: this.data.fieldName,
			searchType: this.getSearchType(),
			value: selectedContact?.id || null,
			selectedContact,
			label: selectedContact?.label,
			displayName: selectedContact?.displayName,
			membershipNumber: selectedContact?.membershipNumber
		};
	}

	validate() {
		if (this.data?.selectedContact?.id) {
			return true;
		}

		return validFormElementValue(null, {
			element: this.inputWrapper || this.rootEl,
			errorMessage: this.data.errorMessage || 'Please select a contact',
			inputType: InputTypes.SELECTOR_INPUT,
			hasIcon: false
		});
	}
}

export default AddNewContact;
